import { useEffect, useState, useCallback } from 'react';
import { useAuth, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import {
  fetchCapstoneStatus,
  saveCapstoneSubmission,
  fetchDiplomaExamQuestions,
  submitExamAttempt,
} from '../../lib/capstoneApi';

const PASS_THRESHOLD = 75;
const MIN_WORDS = 1500;

// Mirrors RUBRIC_CRITERIA in the backend's routes/capstone.js.
const RUBRIC_LABELS = [
  { key: 'argument', label: 'Argument' },
  { key: 'sources', label: 'Sources' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'counterarguments', label: 'Counterarguments' },
];

const CSS = `
  .cap-section {
    margin: 48px 0;
    padding: 28px 32px;
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 4px;
  }
  .cap-section h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 24px;
    color: ${theme.textPrimary};
    margin: 0 0 8px;
  }
  .cap-section .sub {
    color: ${theme.textMuted};
    font-size: 15px;
    margin: 0 0 20px;
  }
  .cap-banner {
    padding: 18px 24px;
    border-radius: 4px;
    margin-bottom: 32px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 14px;
    letter-spacing: 0.03em;
  }
  .cap-banner.awarded {
    background: rgba(var(--ap-success-rgb), 0.12);
    border: 1px solid ${theme.success};
    color: ${theme.success};
  }
  .cap-banner.pending {
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    color: ${theme.textMuted};
  }
  .gate-row { display: flex; gap: 24px; margin-top: 10px; flex-wrap: wrap; }
  .gate {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.05em;
  }
  .gate .status { font-weight: 600; }
  .gate .status.yes { color: ${theme.success}; }
  .gate .status.no { color: ${theme.textMuted}; }

  .exam-progress { color: ${theme.textMuted}; font-size: 13px; margin-bottom: 12px; font-family: 'Josefin Sans', sans-serif; }
  .exam-q { font-size: 18px; color: ${theme.textPrimary}; margin-bottom: 18px; line-height: 1.5; }
  .exam-option {
    display: block;
    width: 100%;
    text-align: left;
    padding: 12px 16px;
    margin-bottom: 8px;
    background: ${theme.bgDark};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 4px;
    color: ${theme.textPrimary};
    cursor: pointer;
    font-family: 'EB Garamond', serif;
    font-size: 15.5px;
  }
  .exam-option:hover:not(:disabled) { border-color: ${theme.gold}; }
  .exam-option.selected { border-color: ${theme.gold}; background: rgba(var(--ap-gold-rgb), 0.12); }
  .exam-option:disabled { cursor: default; }

  .attempt-row {
    display: flex;
    justify-content: space-between;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12.5px;
    color: ${theme.textMuted};
    padding: 6px 0;
    border-bottom: 1px solid rgba(var(--ap-gold-rgb), 0.12);
  }

  .cap-form label {
    display: block;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin: 20px 0 8px;
  }
  .cap-form input[type="text"], .cap-form textarea {
    width: 100%;
    background: ${theme.bgDark};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    border-radius: 4px;
    padding: 10px 12px;
    color: ${theme.textPrimary};
    font-family: 'EB Garamond', serif;
    font-size: 16px;
    box-sizing: border-box;
  }
  .cap-form textarea { min-height: 320px; line-height: 1.6; resize: vertical; }
  .word-count { font-size: 12px; color: ${theme.textMuted}; margin-top: 6px; font-family: 'Josefin Sans', sans-serif; }
  .source-row { display: flex; gap: 8px; margin-bottom: 8px; }
  .source-row input { flex: 1; }
  .form-actions { display: flex; gap: 12px; margin-top: 24px; align-items: center; }
  .save-msg { font-size: 13px; color: ${theme.success}; font-family: 'Josefin Sans', sans-serif; }
  .err-msg { font-size: 13px; color: ${theme.error}; font-family: 'Josefin Sans', sans-serif; }
`;

function StatusView({ status }) {
  if (!status) return null;
  const essayStatus = status.submission?.status || 'not started';
  return (
    <div className={`cap-banner ${status.diplomaAwarded ? 'awarded' : 'pending'}`}>
      {status.diplomaAwarded ? (
        <strong>Diploma awarded.</strong>
      ) : (
        <strong>Diploma in progress.</strong>
      )}
      <div className="gate-row">
        <div className="gate">
          Applied research project: <span className={`status ${essayStatus === 'passed' ? 'yes' : 'no'}`}>{essayStatus.replace('_', ' ')}</span>
        </div>
        <div className="gate">
          Comprehensive exam: <span className={`status ${status.examPassed ? 'yes' : 'no'}`}>
            {status.examPassed ? `passed (${status.bestExamAttempt.score}%)` : 'not passed yet'}
          </span>
        </div>
      </div>
    </div>
  );
}

// Both terminal gates require the same coursework (ADR-0011), so both say so
// the same way. Advisory only -- the server refuses independently either way.
function CourseworkBanner({ what, coursework }) {
  if (!coursework || !Number.isFinite(coursework.totalLessons)) return null;
  return (
    <div className="cap-banner pending">
      <div>
        {what} opens once you have completed {coursework.requiredLessons} of the
        curriculum's {coursework.totalLessons} lessons.
      </div>
      <div style={{ color: theme.textMuted, fontSize: '14px', marginTop: '6px' }}>
        You have marked {coursework.completedLessons} complete. Mark a lesson complete at the
        bottom of its page.
      </div>
    </div>
  );
}

function DiplomaExam({ getToken, onAttemptRecorded, eligibility }) {
  const [phase, setPhase] = useState('idle'); // idle | loading | active | grading | finished
  const [questions, setQuestions] = useState([]);
  const [examToken, setExamToken] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});   // questionId -> option index
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const start = async () => {
    setPhase('loading');
    setError(null);
    setResult(null);
    try {
      const { questions: qs, examToken: token } = await fetchDiplomaExamQuestions(getToken);
      setQuestions(qs);
      setExamToken(token);
      setCurrent(0);
      setAnswers({});
      setPhase('active');
    } catch (err) {
      // The backend's own message, not a generic one: the most common failure
      // here is now the retake cooldown, and "Failed to load exam questions"
      // reads as a broken site when the real answer is "come back tomorrow".
      setError(err.message || 'Failed to load exam questions.');
      setPhase('idle');
    }
  };

  // No right/wrong feedback during the paper: the answer key is no longer sent
  // to the browser (ADR-0010), and an exam that marks itself as you go is a
  // practice quiz. Selections stay changeable until submission.
  const choose = (questionId, index) => {
    setAnswers((prev) => ({ ...prev, [questionId]: index }));
  };

  const submit = async () => {
    setPhase('grading');
    setError(null);
    try {
      const attempt = await submitExamAttempt(getToken, { examToken, answers });
      setResult(attempt);
      onAttemptRecorded(attempt);
      setPhase('finished');
    } catch (err) {
      setError(err.message || 'Submitting the exam failed.');
      setPhase('active');
    }
  };

  if (phase === 'finished' && result) {
    const passed = result.score >= PASS_THRESHOLD;
    return (
      <div>
        <p style={{ fontSize: '20px', color: passed ? theme.success : theme.error }}>
          {result.correctCount} / {result.totalQuestions} correct ({result.score}%) - {passed ? 'Passed' : 'Not passed'}
        </p>
        {result.levelBreakdown?.length > 0 && (
          <p style={{ color: theme.textMuted, fontSize: '14px' }}>
            {result.levelBreakdown
              .sort((a, b) => a.level - b.level)
              .map((l) => `Level ${l.level}: ${l.correct}/${l.total}`)
              .join('  ·  ')}
          </p>
        )}
        <button type="button" className="btn-secondary" onClick={start}>Retake Exam</button>
      </div>
    );
  }

  if (phase === 'active' || phase === 'grading') {
    const q = questions[current];
    const chosen = answers[q._id];
    const answeredCount = Object.keys(answers).length;
    const isLast = current + 1 >= questions.length;
    return (
      <div>
        <div className="exam-progress">
          Question {current + 1} of {questions.length} · {answeredCount} answered
        </div>
        <div className="exam-q">{q.question}</div>
        {q.options.map((opt, i) => (
          <button
            key={i}
            type="button"
            className={`exam-option${chosen === i ? ' selected' : ''}`}
            onClick={() => choose(q._id, i)}
            disabled={phase === 'grading'}
          >
            {opt}
          </button>
        ))}
        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
          {current > 0 && (
            <button type="button" className="btn-secondary" onClick={() => setCurrent((c) => c - 1)} disabled={phase === 'grading'}>
              Back
            </button>
          )}
          {!isLast && (
            <button type="button" className="btn-primary" onClick={() => setCurrent((c) => c + 1)} disabled={phase === 'grading'}>
              Next
            </button>
          )}
          {isLast && (
            <button type="button" className="btn-primary" onClick={submit} disabled={phase === 'grading'}>
              {phase === 'grading' ? 'Grading...' : `Submit exam (${answeredCount}/${questions.length} answered)`}
            </button>
          )}
        </div>
        {error && <p className="err-msg">{error}</p>}
      </div>
    );
  }

  // The server refuses regardless (lib/examEligibility.js); this only saves the
  // student from clicking Start to find out. Anything other than incomplete
  // coursework -- the cooldown, an already-passed exam -- is left to the
  // existing flow, which already reports it accurately after the click.
  const blocked = eligibility && eligibility.code === 'coursework_incomplete';

  return (
    <div>
      <p style={{ color: theme.textMuted, fontSize: '15px' }}>
        50 questions drawn across all three levels, weighted by each level's topic weightage.
        {' '}{PASS_THRESHOLD}% required to pass. Answers are graded when you submit, and you can
        change any answer before then. You can retake it.
      </p>
      {blocked && <CourseworkBanner what="The comprehensive exam" coursework={eligibility} />}
      {error && <p className="err-msg">{error}</p>}
      <button
        type="button"
        className="btn-primary"
        onClick={start}
        disabled={phase === 'loading' || blocked}
      >
        {phase === 'loading' ? 'Loading...' : 'Start Exam'}
      </button>
    </div>
  );
}

function EssayForm({ getToken, submission, onSaved, coursework }) {
  const [title, setTitle] = useState(submission?.title || '');
  const [questionStatement, setQuestionStatement] = useState(submission?.questionStatement || '');
  const [essayText, setEssayText] = useState(submission?.essayText || '');
  const [sources, setSources] = useState(submission?.sourcesCited?.length ? submission.sourcesCited : ['', '', '']);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const words = essayText.trim() ? essayText.trim().split(/\s+/).length : 0;

  const updateSource = (i, val) => {
    setSources((s) => s.map((x, idx) => (idx === i ? val : x)));
  };
  const addSource = () => setSources((s) => [...s, '']);
  const removeSource = (i) => setSources((s) => s.filter((_, idx) => idx !== i));

  const save = async (submit) => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const doc = await saveCapstoneSubmission(getToken, {
        title,
        questionStatement,
        essayText,
        sourcesCited: sources.filter(Boolean),
        submit,
      });
      setMessage(submit ? 'Submitted for review.' : 'Draft saved.');
      onSaved(doc);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const readOnly = submission?.status === 'passed';
  // Drafts are never gated -- the essay is written *during* the curriculum.
  // Only the hand-in waits for the coursework.
  const submitBlocked = coursework ? coursework.complete === false : false;

  return (
    <div className="cap-form">
      {readOnly && <p style={{ color: theme.success, fontSize: '14px' }}>This submission has passed review. You can still revise it, but it's already complete.</p>}
      {/* Shown on both outcomes, not just needs_revision -- a student whose
          essay passed has still earned the reviewer's reasoning. */}
      {['needs_revision', 'passed'].includes(submission?.status) && submission.reviewerNotes && (
        <div className={`cap-banner ${submission.status === 'passed' ? 'awarded' : 'pending'}`}>
          <strong>Reviewer notes:</strong> {submission.reviewerNotes}
        </div>
      )}
      {submission?.rubricScores && Object.values(submission.rubricScores).some((v) => typeof v === 'number') && (
        <p style={{ color: theme.textMuted, fontSize: '14px', margin: '0 0 20px' }}>
          {RUBRIC_LABELS
            .filter(({ key }) => typeof submission.rubricScores[key] === 'number')
            .map(({ key, label }) => `${label} ${submission.rubricScores[key]}/5`)
            .join('  ·  ')}
        </p>
      )}

      <label>Title</label>
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A short title for your analysis" />

      <label>Question statement</label>
      <input
        type="text"
        value={questionStatement}
        onChange={(e) => setQuestionStatement(e.target.value)}
        placeholder="A single sentence a skeptical reader could disagree with"
      />

      <label>Essay ({words} words {words < MIN_WORDS ? `- ${MIN_WORDS} minimum to submit` : ''})</label>
      <textarea value={essayText} onChange={(e) => setEssayText(e.target.value)} placeholder="2,000-3,000 words, applying the course's tools to your question." />

      <label>Sources cited (at least 3)</label>
      {sources.map((s, i) => (
        <div key={i} className="source-row">
          <input type="text" value={s} onChange={(e) => updateSource(i, e.target.value)} placeholder={`Source ${i + 1}`} />
          {sources.length > 3 && (
            <button type="button" className="btn-secondary" onClick={() => removeSource(i)}>Remove</button>
          )}
        </div>
      ))}
      <button type="button" className="btn-secondary" onClick={addSource}>+ Add source</button>

      {submitBlocked && <CourseworkBanner what="Submitting for review" coursework={coursework} />}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={() => save(false)} disabled={saving}>Save Draft</button>
        <button type="button" className="btn-primary" onClick={() => save(true)} disabled={saving || submitBlocked}>Submit for Review</button>
        {message && <span className="save-msg">{message}</span>}
        {error && <span className="err-msg">{error}</span>}
      </div>
    </div>
  );
}

function CapstonePage() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  const loadStatus = useCallback(() => {
    if (!isSignedIn) return;
    fetchCapstoneStatus(getToken).then(setStatus).catch(() => setError(true));
  }, [getToken, isSignedIn]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Capstone - Austrian Process';
    if (isLoaded) loadStatus();
  }, [isLoaded, loadStatus]);

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="Capstone"
        description="The two gates the Diploma itself is awarded on: an applied research essay and a comprehensive exam across all three levels."
      />

      {!isLoaded && <p style={{ color: theme.textMuted }}>Loading...</p>}

      {isLoaded && !isSignedIn && (
        <SignInButton mode="modal">
          <button type="button" className="btn-primary">Sign in to access the Capstone</button>
        </SignInButton>
      )}

      {isLoaded && isSignedIn && (
        <>
          {error && <p className="err-msg">Couldn't load your capstone status.</p>}
          <StatusView status={status} />

          <div className="cap-section">
            <h2>Comprehensive Diploma Exam</h2>
            <p className="sub">Cumulative across Levels 1-3.</p>
            <DiplomaExam
              getToken={getToken}
              onAttemptRecorded={loadStatus}
              eligibility={status?.examEligibility}
            />
          </div>

          <div className="cap-section">
            <h2>Applied Research Project</h2>
            <p className="sub">2,000-3,000 words, citing at least 3 primary sources. Human-reviewed.</p>
            <EssayForm
              getToken={getToken}
              submission={status?.submission}
              onSaved={loadStatus}
              coursework={status?.coursework}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default CapstonePage;
